import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidatorFn} from '@angular/forms';
import {Observable, of} from 'rxjs';
import {debounceTime, filter, finalize, map, startWith} from 'rxjs/operators';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {ConfigAPIService} from '../../../../services/api/config-api.service';

@Component({
  selector: 'app-vehicle-model-autocomplete',
  templateUrl: './vehicle-model-autocomplete.component.html',
  styleUrls: ['./vehicle-model-autocomplete.component.scss'],
  standalone: false
})
export class VehicleModelAutocompleteComponent implements OnInit, OnChanges {

  @Input() control: AbstractControl;
  @Input() disable = false;

  @Input('vehicleModelUuid') initialModelUuid = '';

  @Output() vehicleModelSelected = new EventEmitter();

  public $filteredBrands: Observable<any[]>;
  public $filteredModels: Observable<any[]>;

  public vehicleFormGroup = new FormGroup({
    brand: new FormControl(),
    model: new FormControl({value: '', disabled: true})
  });

  public brandsLoading = false;
  public modelsLoading = false;

  public isPreloading = false;

  private selectedBrand: any;
  private selectedModel: any;

  private debounceTime = 250;

  constructor(private configAPI: ConfigAPIService, private ref: ChangeDetectorRef) {
  }

  get brandControl(): FormControl {
    return this.vehicleFormGroup.get('brand') as FormControl;
  }

  get modelControl(): FormControl {
    return this.vehicleFormGroup.get('model') as FormControl;
  }

  ngOnInit() {
    const validators: ValidatorFn | ValidatorFn[] = this.control.validator ?? null;

    if (this.control.disabled) {
      this.vehicleFormGroup.disable();
      this.ref.detectChanges();
    }

    this.brandControl.setValidators(validators);
    this.modelControl.setValidators(validators);

    this.setupControl(this.brandControl)
      // ponytail: '' loads all ~340 brands; endpoint is unpaginated reference data.
      // One request per component instance — shareReplay in ConfigAPIService if that ever matters.
      .pipe(startWith(''))
      .subscribe((q) => {
        this.brandsLoading = true;
        this.runBrandsQuery(q);
        this.ref.markForCheck();
      });

    this.setupControl(this.modelControl)
      .subscribe((q) => {
        this.modelsLoading = true;
        this.runModelsQuery(q);
        this.ref.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disable) {
      if (changes.disable.currentValue && this.control.disabled) {
        this.brandControl.disable();
        this.modelControl.disable();
      } else {
        this.brandControl.enable();

        if (this.selectedBrand) {
          this.modelControl.enable();
        }
      }
    }

    if (changes.initialModelUuid && changes.initialModelUuid.currentValue) {
      this.isPreloading = true;
      this.preloadModel(this.initialModelUuid);
    }

    this.ref.markForCheck();
  }

  display(item?: any) {
    return item ? item.name : '';
  }

  onBrandSelected($event: MatAutocompleteSelectedEvent) {
    this.selectedBrand = null;
    this.selectedBrand = $event.option.value;

    if (this.selectedBrand) {
      this.setupModelControl();
      this.$filteredModels = of();
    } else {
      this.disableModel();
    }

    this.vehicleModelSelected.emit(null);
  }

  onModelSelected($event: MatAutocompleteSelectedEvent) {
    this.selectedModel = null;
    this.selectedModel = $event.option.value;
    this.vehicleModelSelected.emit({
      ...$event.option.value,
      vehicleBrand: this.selectedBrand
    });
  }

  private runBrandsQuery(q: any) {
    if (!this.queryIsString(q)) {
      this.brandsLoading = false;
      return;
    }

    this.selectedBrand = null;

    this.disableModel();

    this.$filteredBrands = this.configAPI.getVehicleBrands(q)
      .pipe(
        // @ts-ignore
        map(data => data.data),
        finalize(() => this.brandsLoading = false)
      );
  }

  private runModelsQuery(q: any) {
    if (!this.queryIsString(q) || !this.selectedBrand) {
      this.modelsLoading = false;
      return;
    }

    this.selectedModel = null;

    this.loadModels(q);
  }

  private loadModels(q: string) {
    this.$filteredModels = this.configAPI.getVehicleModels(this.selectedBrand.vehicleBrandUuid, q)
      .pipe(
        // @ts-ignore
        map(data => data.data),
        finalize(() => this.modelsLoading = false)
      );
  }

  private setupModelControl() {
    this.selectedModel = null;
    this.modelControl.setValue('');
    this.modelControl.enable();
  }

  private disableModel() {
    this.selectedModel = null;
    this.modelControl.setValue('');
    this.modelControl.disable();
  }

  private setupControl(control: FormControl) {
    return control.valueChanges.pipe(
      debounceTime(this.debounceTime)
    );
  }

  private queryIsString(q: any) {
    return typeof q === 'string';
  }

  private preloadModel(modelUuid: string) {
    // @ts-ignore
    this.configAPI.getVehicleModel(modelUuid).pipe(map(data => data.data))
      .subscribe((data) => {
        this.selectedBrand = data.vehicleBrand;
        if (!this.control.disabled) {
          this.brandControl.enable();
        }
        this.brandControl.setValue(this.selectedBrand);

        this.selectedModel = data;
        if (!this.control.disabled) {
          this.modelControl.enable();
        }
        this.modelControl.setValue(this.selectedModel);
        this.loadModels('');

        this.isPreloading = false;
        this.ref.markForCheck();
      });
  }

  onBrandBlur() {
    if (!this.selectedBrand) {
      this.brandControl.setValue(null);
      this.vehicleModelSelected.emit(null);
      this.ref.markForCheck();
    }
  }

  onModelBlur() {
    if (!this.selectedModel) {
      this.modelControl.setValue(null);
      this.vehicleModelSelected.emit(null);
      this.ref.markForCheck();
    }
  }
}
